// ============================================================
// MOONSHINE MASTERS — moonshineBrewers.js        THE OTHER BREWERS IN THE WOODS
//
// caP'n, 2026-10-24: "then we add co-op via first adding The Mark proper". Co-op step 1: you can SEE each
// other. Borrowed straight from Hooked (hookedOnline.js: useAnglerSync / useRemoteAnglers / RemoteAnglers /
// NameTag - the borrow-first rule). WHICH server you are on is ieServers' job (a Moonshine server holds 4);
// this file is only what a Moonshine server CONTAINS - the other brewers:
//
//   games/moonshine/servers/{sid}/brewers/{uid}
//     { n, x, z, a, s, g, j, ts }   name · position · yaw · moving · body (f|m) · when I arrived · server clock
//   (j picks THE YARD KEEPER: the first brewer in keeps the yard - moonshineYard.js)
//
// ⚑ SEND ONLY WHAT MOVED (10/s at most while moving, one beat per 4 s standing still).
// ⚑ NEVER IN THE RENDER LOOP: the listener writes refs; frames only lerp toward them (ie-realtime-perf).
// ⚑ NO LANTERN ON A GUEST: every pointLight is a light every material pays for. Only YOU carry one.
// ⚠ PLACEHOLDER (scaffolding first): the yard, the still and the beasts are still per-player. Step 2 is the
//   shared yard (one still, one row of jars, everyone's hands on it).
// ============================================================
import { useEffect, useMemo, useRef, useState, memo } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { ref, set, onValue, onDisconnect, remove, serverTimestamp } from "firebase/database";
import { db } from "./firebase";
import { serverNow } from "./ieSocial";
import { Hero } from "./Hero";
import { C } from "./theme";
import { MOONSHINE_BASE } from "./moonshineOnline";

export const brewersPath = (sid) => `${MOONSHINE_BASE}/servers/${sid}/brewers`;

const SEND_MS = 100;      // ⟵ DIAL  fastest position send
const BEAT_MS = 4000;     // ⟵ DIAL  standing still: one "still here" per beat
const STALE_MS = 75000;   // ⟵ DIAL  a brewer silent this long has gone home (long: a background tab beats once a minute; a real goodbye is onDisconnect)
const MAX_SHOWN = 3;      // ⟵ DIAL  a Moonshine server holds 4: you + 3

// when I arrived on each server, kept OUTSIDE the send effect: changing your body or your name mid-run must not
// make you "arrive" again (that would hand the yard to someone else). A reload is a real new arrival.
const ARRIVED = {};
export const arrivedAt = (sid) => (ARRIVED[sid] = ARRIVED[sid] || Math.round(serverNow()));

// ── SENDING: me ──
export function useBrewerSync({ sid, uid, name, posRef, yawRef, speedRef, gender, eyesRef }) {
  // ⚑ (2026-10-25) a new NAME or BODY must not leave and re-enter the server: the effect below removes my row on cleanup, and a
  //   vanished row for even a moment hands the yard to the next brewer in (and a friend's satchel to the wrong shelf). Name and body
  //   ride in a ref; changing them only forces one fresh beat.
  const meta = useRef({ name, gender }); meta.current = { name, gender };
  const beat = useRef(null);
  useEffect(() => { if (beat.current) beat.current(true); }, [name, gender]);
  useEffect(() => {
    if (!sid || !uid) return undefined;
    const r = ref(db, `${brewersPath(sid)}/${uid}`);
    let last = null, lastAt = 0, off = false;
    const j = arrivedAt(sid);                           // when I arrived on this server (fixed for this visit)
    const send = (force) => {
      if (off) return;
      const p = posRef.current; if (!p) return;
      const now = performance.now();
      const row = {
        n: String(meta.current.name || "Brewer").slice(0, 24),
        x: +p.x.toFixed(2), z: +p.z.toFixed(2), a: +(yawRef.current || 0).toFixed(2),
        s: speedRef.current > 0.05 ? 1 : 0, g: meta.current.gender === "m" ? "m" : "f",
        e: eyesRef && eyesRef.current ? 1 : 0,        // the Unstable Eyes are open: the keeper's guards hunt me with two beasts at double speed
      };
      const moved = !last || row.x !== last.x || row.z !== last.z || row.a !== last.a || row.s !== last.s || row.g !== last.g || row.e !== last.e || row.n !== last.n;
      if (!force && (!moved || now - lastAt < SEND_MS)) return;
      last = row; lastAt = now;
      set(r, { ...row, j, ts: serverTimestamp() }).catch(() => {});
    };
    beat.current = send;
    onDisconnect(r).remove().then(() => send(true)).catch(() => send(true));   // onDisconnect FIRST (ieSocial's ordering law)
    const t = setInterval(() => send(false), SEND_MS);
    const hb = setInterval(() => send(true), BEAT_MS);
    return () => {
      off = true; beat.current = null; clearInterval(t); clearInterval(hb);
      delete ARRIVED[sid];                              // a real goodbye: coming back later is a NEW arrival (last in, not first)
      onDisconnect(r).cancel().catch(() => {});
      remove(r).catch(() => {});
    };
  }, [sid, uid, posRef, yawRef, speedRef, eyesRef]);
}

// ── RECEIVING: everyone else ── a stable store { ids (state: changes only on arrive/leave), map (ref) }
export function useRemoteBrewers(sid, uid) {
  const map = useRef(new Map());
  const [ids, setIds] = useState([]);
  const rawRef = useRef({});
  useEffect(() => {
    map.current.clear(); setIds([]); rawRef.current = {};
    if (!sid || !uid) return undefined;
    const apply = () => {
      const now = serverNow(), keep = [];
      for (const [id, a] of Object.entries(rawRef.current)) {
        if (id === uid || !a || typeof a.x !== "number" || typeof a.z !== "number") continue;
        if (!a.ts || now - a.ts > STALE_MS) continue;
        keep.push(id);
        let e = map.current.get(id);
        if (!e) {
          e = { id, pos: { current: new THREE.Vector3(a.x, 0, a.z) }, yaw: { current: a.a || 0 }, speed: { current: 0 },
            target: new THREE.Vector3(a.x, 0, a.z), targetYaw: a.a || 0 };
          map.current.set(id, e);
        }
        e.target.set(a.x, 0, a.z); e.targetYaw = a.a || 0; e.speed.current = a.s ? 1 : 0;
        e.eyes = a.e === 1; e.name = a.n || "Brewer"; e.g = a.g === "m" ? "m" : "f";
      }
      for (const id of [...map.current.keys()]) if (!keep.includes(id)) map.current.delete(id);
      const next = keep.sort().slice(0, MAX_SHOWN);
      setIds((prev) => {
        const sig = (l) => l.map((id) => `${id}:${map.current.get(id)?.g}:${map.current.get(id)?.name}`).join("|");
        return sig(prev) === sig(next) ? prev : next;
      });
    };
    const offV = onValue(ref(db, brewersPath(sid)), (snap) => { rawRef.current = snap.val() || {}; apply(); }, () => {});
    const t = setInterval(apply, 5000);           // a closed laptop sends no event - age them out
    return () => { offV(); clearInterval(t); };
  }, [sid, uid]);
  return useMemo(() => ({ ids, map }), [ids]);
}

// ── DRAWING THEM ──
function angleLerp(a, b, k) {
  let d = ((b - a + Math.PI) % (Math.PI * 2)) - Math.PI;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * k;
}

function NameTag({ name }) {
  const tex = useMemo(() => {
    const c = document.createElement("canvas"); c.width = 256; c.height = 64;
    const x = c.getContext("2d");
    x.font = "600 30px Georgia, serif"; x.textAlign = "center"; x.textBaseline = "middle";
    x.shadowColor = "rgba(0,0,0,0.85)"; x.shadowBlur = 8; x.fillStyle = C.sceneHi || "#f6c97a";   // a friend's name floats over the dark woods: the SCENE gold, not paper ink
    x.fillText(String(name).slice(0, 18), 128, 32);
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
  }, [name]);
  useEffect(() => () => tex.dispose(), [tex]);
  return (
    <sprite position={[0, 2.25, 0]} scale={[1.6, 0.4, 1]} raycast={() => null}>
      <spriteMaterial map={tex} transparent depthWrite={false} fog={false} />
    </sprite>
  );
}

// `name` passed separately: `entry` is mutated in place, so memo would never see a rename through it
const RemoteBrewer = memo(function RemoteBrewer({ entry, name }) {
  const tag = useRef();
  useFrame((_, dtRaw) => {
    const k = 1 - Math.exp(-Math.min(dtRaw, 0.1) * 10);
    entry.pos.current.lerp(entry.target, k);
    entry.yaw.current = angleLerp(entry.yaw.current, entry.targetYaw, k);
    if (tag.current) tag.current.position.copy(entry.pos.current);
  });
  return (
    <>
      <Hero gender={entry.g} posRef={entry.pos} yawRef={entry.yaw} speedRef={entry.speed} lantern={false} />
      <group ref={tag}><NameTag name={name} /></group>
    </>
  );
});

export function RemoteBrewers({ store }) {
  if (!store || !store.ids.length) return null;
  return store.ids.map((id) => {
    const e = store.map.current.get(id);
    return e ? <RemoteBrewer key={`${id}:${e.g}`} entry={e} name={e.name} /> : null;
  });
}
